import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Building2,
  TrendingUp,
  TrendingDown,
  Activity,
  PieChart,
  BarChart3,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  ChevronRight,
  RefreshCw,
  Eye,
  EyeOff,
  Briefcase,
  Landmark,
  CircleDollarSign,
  LineChart,
  CandlestickChart,
  Coins,
  Home,
  Rocket,
  Target,
  FileText,
  Users,
  Globe,
  Shield,
  Sparkles,
  DollarSign,
  FileCheck,
  Clock,
  Layers,
  Cpu,
  Music,
  Star,
  Plus,
  Trash2,
  Lock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useAppData } from '@/context/AppDataContext';
import { formatCurrencySpaced, maskCurrency } from '@/lib/market';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Cell, CartesianGrid,
} from 'recharts';

import {
  DONUT_COLORS, PM_CATS, ALT_CATS, rebaseAllocationExcludingFD, isIofRecord, buildAllocation,
} from '@/lib/netWorthAllocation';

const API = process.env.REACT_APP_BACKEND_URL + '/api';


const formatPercent = (value) => {
  if (value === undefined || value === null) return '0.00%';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
};

// Simple inline SVG price history chart for the watchlist detail popup —
// deliberately dependency-free (no recharts) since it only needs a single
// line + area fill over a date/close series.
const StockHistoryChart = ({ data, height = 200, width = 760 }) => {
  if (!data || data.length < 2) {
    return <div className="text-xs text-[#a8a8a8] text-center py-10">Not enough history to chart yet.</div>;
  }
  const pad = { t: 12, r: 12, b: 24, l: 56 };
  const cw = width - pad.l - pad.r;
  const ch = height - pad.t - pad.b;
  const vals = data.map(d => d.close);
  const minV = Math.min(...vals) * 0.98;
  const maxV = Math.max(...vals) * 1.02;
  const xs = (i) => pad.l + (i / (data.length - 1)) * cw;
  const ys = (v) => pad.t + ch - ((v - minV) / (maxV - minV || 1)) * ch;
  const lineD = data.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xs(i)} ${ys(p.close)}`).join(' ');
  const areaD = `${lineD} L ${xs(data.length - 1)} ${pad.t + ch} L ${pad.l} ${pad.t + ch} Z`;
  const isUp = data[data.length - 1].close >= data[0].close;
  const lineColor = isUp ? '#059669' : '#e53535';
  const yLabels = [minV, (minV + maxV) / 2, maxV];
  const xTickIdx = [0, Math.floor(data.length / 2), data.length - 1];
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
      <defs>
        <linearGradient id="stockHistGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={lineColor} stopOpacity="0.22" />
          <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
        </linearGradient>
      </defs>
      {yLabels.map((v, i) => (
        <g key={i}>
          <line x1={pad.l} y1={ys(v)} x2={width - pad.r} y2={ys(v)} stroke="#f0f0f0" strokeDasharray="4" />
          <text x={pad.l - 8} y={ys(v)} textAnchor="end" dominantBaseline="middle" fontSize="10" fill="#a8a8a8">
            {v.toFixed(2)}
          </text>
        </g>
      ))}
      <path d={areaD} fill="url(#stockHistGrad)" />
      <path d={lineD} fill="none" stroke={lineColor} strokeWidth="2" strokeLinejoin="round" />
      {xTickIdx.map((i) => (
        <text key={i} x={xs(i)} y={height - 4} textAnchor="middle" fontSize="10" fill="#a8a8a8">
          {data[i]?.date?.slice(5) || ''}
        </text>
      ))}
    </svg>
  );
};

const RETURN_LABELS = { daily: '1D', weekly: '1W', monthly: '1M', yearly: '1Y' };

const ASSET_LABEL_MAP = {
  stocks: 'Equities', mf: 'Mutual Funds', fd: 'Fixed Deposits',
  bonds: 'Bonds', etf: 'ETFs', fo: 'Futures & Options',
  nps: 'National Pension System', pms: 'Portfolio Management Services',
  aif: 'Alternative Investment Funds', ppf: 'PPF / EPF',
  gold: 'Gold / Silver', realestate: 'Real Estate',
};

// Simple Donut Chart
const DonutChart = ({ data, size = 200 }) => {
  const [hovered, setHovered] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  if (!data || data.length === 0) return null;

  const total = data.reduce((sum, item) => sum + item.value, 0);
  let currentAngle = -90;
  const radius = size / 2 - 14;
  const cx = size / 2;
  const cy = size / 2;
  const innerRadius = radius * 0.52;

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <div className="relative mx-auto" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        className="cursor-pointer"
        onMouseLeave={() => setHovered(null)}
        onMouseMove={handleMouseMove}
      >
        {data.map((item, index) => {
          const percentage = (item.value / total) * 100;
          const angle = (percentage / 100) * 360;
          const startAngle = currentAngle;
          const endAngle = currentAngle + angle;
          currentAngle = endAngle;

          const opacity = hovered === null || hovered === index ? 1 : 0.35;

          // Full circle — SVG arc paths are degenerate at 360°
          if (angle >= 359.9) {
            return (
              <g key={index} onMouseEnter={() => setHovered(index)} style={{ opacity, transition: 'opacity 0.15s' }}>
                <circle cx={cx} cy={cy} r={radius} fill={item.color} />
                <circle cx={cx} cy={cy} r={innerRadius} fill="white" />
              </g>
            );
          }

          const startRad = (startAngle * Math.PI) / 180;
          const endRad = (endAngle * Math.PI) / 180;

          const x1 = cx + radius * Math.cos(startRad);
          const y1 = cy + radius * Math.sin(startRad);
          const x2 = cx + radius * Math.cos(endRad);
          const y2 = cy + radius * Math.sin(endRad);
          const x3 = cx + innerRadius * Math.cos(endRad);
          const y3 = cy + innerRadius * Math.sin(endRad);
          const x4 = cx + innerRadius * Math.cos(startRad);
          const y4 = cy + innerRadius * Math.sin(startRad);

          const largeArcFlag = angle > 180 ? 1 : 0;

          const pathData = [
            `M ${x1} ${y1}`,
            `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
            `L ${x3} ${y3}`,
            `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}`,
            'Z'
          ].join(' ');

          return (
            <path
              key={index}
              d={pathData}
              fill={item.color}
              stroke="none"
              style={{ opacity, transition: 'opacity 0.15s' }}
              onMouseEnter={() => setHovered(index)}
            />
          );
        })}
      </svg>
      {hovered !== null && data[hovered] && (
        <div
          className="absolute z-50 pointer-events-none bg-white border border-[#e5e5e5] text-[#1a1a1a] text-xs px-2.5 py-1.5 whitespace-nowrap rounded-lg shadow-md"
          style={{
            left: tooltipPos.x + 10,
            top: tooltipPos.y - 38,
            transform: tooltipPos.x > size * 0.6 ? 'translateX(-100%)' : 'none',
          }}
        >
          <span className="font-medium text-[#c97a3a]">{data[hovered].name}</span>
          <span className="ml-2">{data[hovered].percentage != null ? data[hovered].percentage.toFixed(1) : data[hovered].value.toFixed(1)}%</span>
        </div>
      )}
    </div>
  );
};

export default function ConsolidatedDashboard() {
  const { user, signOut } = useAuth();
  const { categoryColorMap } = useAppData();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showValues, setShowValues] = useState(true);

  const formatCurrency = (value, sv = true) => {
    if (!sv) return maskCurrency(user?.market);
    return formatCurrencySpaced(value, user?.market);
  };

  // Data states
  const [publicMarkets, setPublicMarkets] = useState(null);
  const [assetClassSummary, setAssetClassSummary] = useState([]);
  const [privateEquity, setPrivateEquity] = useState({ companies: [], deals: [], totalValue: 0 });
  const [realEstate, setRealEstate] = useState({ properties: [], totalValue: 0, totalEquity: 0 });
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [totalNetWorth, setTotalNetWorth] = useState(0);
  const [assetAllocation, setAssetAllocation] = useState([]);
  const [pmData, setPmData] = useState({});
  const [altData, setAltData] = useState({});
  const [pmAUMTotal, setPmAUMTotal] = useState(0);
  const [cashAltAUMTotal, setCashAltAUMTotal] = useState(0);
  const [liquidReserveAUMTotal, setLiquidReserveAUMTotal] = useState(0);
  const [performanceYTD, setPerformanceYTD] = useState(null);
  const [performance3Y, setPerformance3Y] = useState(null);
  const [benchmarks, setBenchmarks] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [watchlistQuotes, setWatchlistQuotes] = useState({});
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  const [watchlistSearch, setWatchlistSearch] = useState('');
  const [watchlistSearchResults, setWatchlistSearchResults] = useState([]);
  const [watchlistSearchLoading, setWatchlistSearchLoading] = useState(false);
  const [watchlistDetailSymbol, setWatchlistDetailSymbol] = useState(null);
  const [watchlistDetail, setWatchlistDetail] = useState(null);
  const [watchlistDetailLoading, setWatchlistDetailLoading] = useState(false);
  const [watchlistDetailChartLoading, setWatchlistDetailChartLoading] = useState(false);
  const [watchlistDetailRange, setWatchlistDetailRange] = useState('1y');
  const [addingPrivate, setAddingPrivate] = useState(false);

  const PM_OTHERS_CATS = ['hedge_fund', 'secondaries', 'special_situations', 'pre_ipo', 'mezzanine', 'fof', 'co_investment', 'royalty', 'natural_resources'];

  const fetchAllData = useCallback(async () => {
    try {
      const [consolidationRes, companiesRes, dealsRes, realEstateRes, approvalsRes, pmResults, altResults, fdRes, acSummaryRes, perfYTDRes, perf3YRes, benchmarksRes] = await Promise.all([
        axios.get(`${API}/analytics/consolidation`).catch(() => ({ data: null })),
        axios.get(`${API}/startups/companies`).catch(() => ({ data: [] })),
        axios.get(`${API}/deals/`).catch(() => ({ data: [] })),
        axios.get(`${API}/real-estate/portfolio`).catch(() => ({ data: null })),
        axios.get(`${API}/deals/ic-memos`).catch(() => ({ data: [] })),
        Promise.all(PM_CATS.map(c => axios.get(`${API}/private-markets/${c}`).then(r => [c, r.data || []]).catch(() => [c, []]))),
        Promise.all(ALT_CATS.map(c => axios.get(`${API}/other-assets/${c}`).then(r => [c, r.data || []]).catch(() => [c, []]))),
        axios.get(`${API}/markets/holdings?asset_class=fd`).catch(() => ({ data: [] })),
        axios.get(`${API}/analytics/exposure/asset_class`).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/analytics/performance?period=ytd`).catch(() => ({ data: null })),
        axios.get(`${API}/analytics/performance?period=3y`).catch(() => ({ data: null })),
        axios.get(`${API}/analytics/performance/all-benchmarks?period=ytd`).catch(() => ({ data: [] })),
      ]);
      setPerformanceYTD(perfYTDRes.data);
      setPerformance3Y(perf3YRes.data);
      setBenchmarks(Array.isArray(benchmarksRes.data) ? benchmarksRes.data : []);
      setAssetClassSummary(acSummaryRes.data?.items || []);
      const pmObjRaw = Object.fromEntries(pmResults);
      // IOF USD Liquid Reserves Fund is a liquid reserve position, not a
      // private-markets holding — carve it out into its own section so it
      // no longer counts toward the Private Markets bucket/graphs.
      const iofRecord = (pmObjRaw.pe || []).find(isIofRecord);
      const pmObj = iofRecord
        ? { ...pmObjRaw, pe: pmObjRaw.pe.filter(d => d !== iofRecord) }
        : pmObjRaw;
      const altObj = Object.fromEntries(altResults);
      const fdHoldings = fdRes.data || [];
      setPmData(pmObj);
      setAltData({ ...altObj, fd: fdHoldings });
      const allMemos = approvalsRes.data || [];
      setPendingApprovals(allMemos.filter(m => ['submitted', 'under_review'].includes(m.status)));
      
      // Public markets — strip FD out (FD lives in Cash & Alternatives)
      const fdValue = fdHoldings.reduce((s, d) => s + (d.current_value || d.avg_price || 0), 0);
      const fdCount = fdHoldings.length;
      const rawPublic = consolidationRes.data || {
        total_value: 0, total_invested: 0, total_gain: 0,
        gain_percent: 0, holdings_count: 0, asset_allocation: {}
      };
      const fdInvested = fdHoldings.reduce((s, d) => s + (d.invested_value || d.avg_price || 0), 0);
      const publicInvestedXFD = (rawPublic.total_invested || 0) - fdInvested;
      const publicGainXFD     = (rawPublic.total_gain     || 0) - (fdValue - fdInvested);
      const publicData = {
        ...rawPublic,
        total_value:    (rawPublic.total_value    || 0) - fdValue,
        total_invested: publicInvestedXFD,
        total_gain:     publicGainXFD,
        // gain % must use the same FD-free base as the value/invested/gain above
        gain_percent:   publicInvestedXFD > 0 ? (publicGainXFD / publicInvestedXFD) * 100 : 0,
        holdings_count: Math.max(0, (rawPublic.holdings_count || 0) - fdCount),
        asset_allocation: rebaseAllocationExcludingFD(rawPublic.asset_allocation),
      };
      setPublicMarkets(publicData);
      
      // Private equity
      const companies = companiesRes.data || [];
      const deals = dealsRes.data || [];

      // Pipeline value = sum of target investment amounts across all deals
      const peValue = deals.reduce((sum, deal) => sum + (deal.target_investment_amount || 0), 0);

      // Portfolio companies = distinct companies that have a deal (not the startups registry)
      const portfolioCompanyCount = new Set(
        deals.map(d => d.company_name || d.company_id).filter(Boolean)
      ).size;

      setPrivateEquity({
        companies,
        deals,
        totalValue: peValue,
        portfolioCompanyCount,
        dealsInProgress: deals.filter(d => d.stage !== 'closed' && d.stage !== 'passed').length,
        closedDeals: deals.filter(d => d.stage === 'closed').length
      });
      
      // Real estate data
      const reData = realEstateRes.data || { properties: [], total_value: 0, total_equity: 0, portfolio_yield: 0 };
      const reValue = reData.total_value || 0;
      setRealEstate({
        properties: reData.properties || [],
        totalValue: reValue,
        totalEquity: reData.total_equity || 0,
        totalAnnualRent: reData.total_annual_rent || 0,
        portfolioYield: reData.portfolio_yield || 0,
        totalGain: reData.total_gain || 0,
        gainPercent: reData.gain_percent || 0
      });
      
      // Calculate total net worth
      const publicValue = publicData.total_value || 0;
      const pmAUM = Object.values(pmObj).flat().reduce((s, d) => s + (d.current_value || d.nav || 0), 0);
      setPmAUMTotal(pmAUM);
      const liquidReserveAUM = iofRecord?.current_value || 0;
      setLiquidReserveAUMTotal(liquidReserveAUM);
      const cashAltAUM =
        fdValue +
        (altObj.cash        || []).reduce((s, d) => s + (d.balance || 0), 0) +
        (altObj.alternatives|| []).reduce((s, d) => s + (d.nav || 0), 0);
      const altAUM = cashAltAUM +
        (altObj.gold        || []).reduce((s, d) => s + (d.units || 0) * (d.current_price || 0), 0) +
        (altObj.art         || []).reduce((s, d) => s + (d.current_value || 0), 0) +
        (altObj.ip          || []).reduce((s, d) => s + (d.current_value || 0), 0);
      setCashAltAUMTotal(cashAltAUM);
      const total = publicValue + pmAUM + altAUM + reValue + liquidReserveAUM;
      setTotalNetWorth(total);
      
      // Build asset allocation (shared with the Portfolio Summary page)
      const othersAUM = reValue +
        (altObj.gold || []).reduce((s, d) => s + (d.units || 0) * (d.current_price || 0), 0) +
        (altObj.art  || []).reduce((s, d) => s + (d.current_value || 0), 0) +
        (altObj.ip   || []).reduce((s, d) => s + (d.current_value || 0), 0);
      const allocation = buildAllocation({
        publicValue,
        publicAssetAllocation: publicData.asset_allocation,
        pmAUM, liquidReserveAUM, cashAltAUM, othersAUM, total,
      });

      setAssetAllocation(allocation);
      
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const fetchWatchlist = useCallback(async () => {
    setWatchlistLoading(true);
    try {
      const res = await axios.get(`${API}/markets/watchlist/quotes`);
      setWatchlist(res.data.items || []);
      setWatchlistQuotes(res.data.quotes || {});
    } catch (err) {
      console.error('Watchlist fetch failed:', err);
    } finally {
      setWatchlistLoading(false);
    }
  }, []);

  useEffect(() => { fetchWatchlist(); }, [fetchWatchlist]);

  const handleWatchlistSearchChange = useCallback(async (query) => {
    setWatchlistSearch(query);
    if (query.trim().length < 1) { setWatchlistSearchResults([]); return; }
    setWatchlistSearchLoading(true);
    try {
      const res = await axios.get(`${API}/markets/market/search`, { params: { q: query, limit: 8 } });
      setWatchlistSearchResults(res.data || []);
    } catch { setWatchlistSearchResults([]); }
    finally { setWatchlistSearchLoading(false); }
  }, []);

  const handleAddWatchlistItem = async (symbol, name, exchange) => {
    try {
      await axios.post(`${API}/markets/watchlist`, {
        symbol: symbol.toUpperCase(),
        name,
        exchange: exchange || 'US',
        is_private: addingPrivate,
      });
      setWatchlistSearch('');
      setWatchlistSearchResults([]);
      setAddingPrivate(false);
      await fetchWatchlist();
      toast.success(`${symbol} added to watchlist`);
    } catch {
      toast.error('Failed to add to watchlist');
    }
  };

  const handleRemoveWatchlist = async (symbol) => {
    try {
      await axios.delete(`${API}/markets/watchlist/${symbol}`);
      await fetchWatchlist();
      toast.success(`${symbol} removed`);
    } catch {
      toast.error('Failed to remove');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    toast.success('Dashboard refreshed');
  };

  // Opening a *new* symbol: clear old data and show the full popup spinner.
  const handleOpenWatchlistDetail = useCallback(async (symbol, range = '1y') => {
    setWatchlistDetailSymbol(symbol);
    setWatchlistDetailRange(range);
    setWatchlistDetailLoading(true);
    setWatchlistDetail(null);
    try {
      const res = await axios.get(`${API}/markets/watchlist/detail/${symbol}`, { params: { range } });
      setWatchlistDetail(res.data);
    } catch (err) {
      toast.error(`Couldn't load ${symbol}`);
    } finally {
      setWatchlistDetailLoading(false);
    }
  }, []);

  // Switching the range on an *already-open* symbol: keep the existing data
  // on screen (price, stats, old chart) and only swap it once the new range
  // has loaded, instead of blanking the whole popup back to a spinner.
  const handleChangeWatchlistRange = useCallback(async (symbol, range) => {
    setWatchlistDetailRange(range);
    setWatchlistDetailChartLoading(true);
    try {
      const res = await axios.get(`${API}/markets/watchlist/detail/${symbol}`, { params: { range } });
      setWatchlistDetail(res.data);
    } catch (err) {
      toast.error(`Couldn't load ${symbol}`);
    } finally {
      setWatchlistDetailChartLoading(false);
    }
  }, []);

  const handleCloseWatchlistDetail = (open) => {
    if (!open) {
      setWatchlistDetailSymbol(null);
      setWatchlistDetail(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-[#ffd87a] animate-spin mx-auto mb-4" />
          <p className="text-[#4a4a4a]">Loading your wealth overview...</p>
        </div>
      </div>
    );
  }

  const publicGainPercent = publicMarkets?.gain_percent || 0;
  const publicValue    = publicMarkets?.total_value    || 0;
  const publicGain     = publicMarkets?.total_gain     || 0;
  const publicInvested = publicMarkets?.total_invested || 0;

  // Cross-portfolio FMV gain
  const pmAllFlat      = Object.values(pmData).flat();
  const pmInvestedAll  = pmAllFlat.reduce((s, d) => s + (d.invested || 0), 0);
  const pmGainAll      = pmAllFlat.reduce((s, d) => s + ((d.current_value || d.nav || 0) - (d.invested || 0)), 0);
  const totalInvestedKnown = publicInvested + pmInvestedAll;
  const totalGainKnown     = publicGain + (pmInvestedAll > 0 ? pmGainAll : 0);
  const fmvGainPct = totalInvestedKnown > 0 ? (totalGainKnown / totalInvestedKnown * 100) : null;

  // Avg IRR across all private market holdings
  const irrEntries = pmAllFlat.filter(d => d.irr != null);
  const avgIRR = irrEntries.length ? irrEntries.reduce((s, d) => s + d.irr, 0) / irrEntries.length : null;

  // Liquidity — public markets, cash, gold and the IOF liquid reserve fund
  // are all readily convertible to cash; everything else (private markets,
  // FDs, real estate, other alternatives) is treated as locked/illiquid.
  const goldV      = (altData.gold || []).reduce((s, d) => s + (d.units || 0) * (d.current_price || 0), 0);
  const cashV      = (altData.cash || []).reduce((s, d) => s + (d.balance || 0), 0);
  const liquidValue = publicValue + cashV + goldV + liquidReserveAUMTotal;
  const liquidPct   = totalNetWorth > 0 ? Math.round(liquidValue / totalNetWorth * 100) : 0;
  const illiquidPct = 100 - liquidPct;

  const closedDealsCount = privateEquity.closedDeals || 0;
  const xirr = publicMarkets?.xirr ?? null;

  const firstName = user?.name
    ? (() => { const p = user.name.split(' '); return p[0].length <= 2 && p.length > 1 ? p[0] + ' ' + p[1] : p[0]; })()
    : null;

  return (
    <div style={{ padding: '28px 36px 52px', background: '#f8f6f1', minHeight: '100vh' }} data-testid="consolidated-dashboard">
      {/* Topbar */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, marginBottom: 18 }}>
        <div>
          <div style={{ textTransform: 'uppercase', fontSize: 10, fontWeight: 600, letterSpacing: '0.13em', color: '#a8a8a8', marginBottom: 6 }}>
            Family Office Overview{user?.name ? ` · ${user.name}` : ''}
          </div>
          <h1 style={{ fontFamily: "'PerfectNineties', Georgia, serif", fontWeight: 500, fontSize: 30, letterSpacing: '-0.02em', lineHeight: 1.1, color: '#2a2a2a', margin: 0 }}>
            {firstName ? `Welcome back, ${firstName}!` : 'Portfolio Overview'}
          </h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 4 }}>
          <span style={{ fontSize: 11.5, color: '#a8a8a8', whiteSpace: 'nowrap' }}>
            As of {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
          </span>
          <button
            onClick={() => setShowValues(!showValues)}
            data-testid="toggle-values-btn"
            style={{ width: 34, height: 34, borderRadius: 9, border: '1px solid #e5e5e5', background: '#fff', display: 'grid', placeItems: 'center', color: '#666', cursor: 'pointer' }}
          >
            {showValues ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </div>

      {/* Hero Card */}
      <section style={{
        display: 'grid', gridTemplateColumns: '1.1fr auto 1.2fr', gap: 0,
        background: '#ffffff', border: '0.6px solid #ffd879', borderRadius: 20,
        boxShadow: '0 2px 18px rgba(0,0,0,0.07)', marginBottom: 22, overflow: 'hidden',
      }}>
        {/* Left — Net Worth */}
        <div style={{ padding: '36px 40px 32px', display: 'flex', flexDirection: 'column', gap: 0, background: 'linear-gradient(96deg, #fcfcf5 0%, #fef3c2 100%)' }}>
          <div style={{ textTransform: 'uppercase', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.13em', color: '#a8a8a8', marginBottom: 10 }}>Total Net Worth</div>
          <div style={{ fontFamily: "'PerfectNineties', Georgia, serif", fontWeight: 500, color: '#000', fontSize: 62, letterSpacing: '-0.03em', lineHeight: 1, margin: '0 0 18px' }}>
            {formatCurrency(totalNetWorth, showValues)}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            {fmvGainPct != null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 99, background: 'rgba(16,185,129,0.12)', color: '#059669', fontSize: 12.5, fontWeight: 600, border: '1px solid rgba(16,185,129,0.30)' }}>
                <TrendingUp size={14} /> {formatPercent(fmvGainPct)} overall
              </span>
            )}
            {xirr != null && (
              <span style={{ fontSize: 13, color: '#666' }}>
                XIRR <strong style={{ color: '#000' }}>{formatPercent(xirr)}</strong>
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 0, borderTop: '1px solid #e5e5e5', marginTop: 28 }}>
            {[
              { label: 'Public Markets', value: formatCurrency(publicValue, showValues) },
              { label: 'Private Markets', value: formatCurrency(pmAUMTotal, showValues) },
              { label: 'Cash & Alts', value: formatCurrency(cashAltAUMTotal, showValues) },
              ...(liquidReserveAUMTotal > 0 ? [{ label: 'IOF Liquid Reserves', value: formatCurrency(liquidReserveAUMTotal, showValues) }] : []),
            ].map((s, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, padding: '18px 0 0', ...(i > 0 ? { borderLeft: '1px solid #e5e5e5', paddingLeft: 22 } : {}) }}>
                <span style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.09em', color: '#a8a8a8', fontWeight: 600 }}>{s.label}</span>
                <span style={{ fontSize: 19, fontWeight: 600, color: '#000', fontVariantNumeric: 'tabular-nums' }}>{s.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div style={{ width: 1, background: 'linear-gradient(to bottom, transparent 10%, #e5e5e5 30%, #e5e5e5 70%, transparent 90%)', alignSelf: 'stretch' }} />

        {/* Right — Allocation */}
        <div style={{ padding: '32px 36px 28px', display: 'flex', flexDirection: 'column', gap: 18, background: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ textTransform: 'uppercase', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.13em', color: '#a8a8a8' }}>Allocation</span>
            <span style={{ fontSize: 12, color: '#a8a8a8' }}>{assetAllocation.length} asset classes</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, flex: 1 }}>
            {assetAllocation.length > 0 && <DonutChart data={assetAllocation} size={186} />}
            <ul style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0 }}>
              {assetAllocation.map((item, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 0', borderBottom: i < assetAllocation.length - 1 ? '1px solid #e5e5e5' : 'none', fontSize: 12.5 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: item.color, flexShrink: 0 }} />
                  <span style={{ flex: 1, color: '#404040', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
                  <span style={{ fontWeight: 600, color: '#1a1a1a', fontVariantNumeric: 'tabular-nums' }}>{item.percentage.toFixed(1)}%</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Stat Band */}
      <div style={{ display: 'flex', flexWrap: 'wrap', background: '#fff', border: '1px solid #e5e5e5', borderRadius: 13, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 18, overflow: 'hidden' }}>
        {[
          { k: 'Public Markets', v: formatCurrency(publicValue, showValues), delta: formatPercent(publicGainPercent), tone: publicGainPercent >= 0 ? '#059669' : '#e53535' },
          { k: 'Unrealised P&L', v: formatCurrency(publicGain, showValues), delta: `on ${formatCurrency(publicInvested, showValues)}`, tone: publicGain >= 0 ? '#059669' : '#e53535' },
          { k: 'Private Markets', v: formatCurrency(pmAUMTotal, showValues), delta: `${closedDealsCount} invested`, tone: '#666' },
          { k: 'Portfolio FMV', v: formatCurrency(totalNetWorth, showValues), delta: fmvGainPct != null ? `${formatPercent(fmvGainPct)} gain` : 'all classes', tone: fmvGainPct != null ? (fmvGainPct >= 0 ? '#059669' : '#e53535') : '#666' },
          { k: 'Liquidity', v: `${liquidPct}%`, delta: `${illiquidPct}% locked`, tone: '#666' },
          { k: 'Pending Approvals', v: String(pendingApprovals.length), delta: 'awaiting review', tone: '#1a1a1a', flag: true },
        ].map((cell, i) => (
          <div key={i} style={{ flex: 1, minWidth: 120, padding: '18px 20px', borderLeft: i > 0 ? '1px solid #e5e5e5' : 'none', background: cell.flag ? 'linear-gradient(96deg, #ffe7a8 0%, #ffd879 100%)' : undefined, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
            <div style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600, color: cell.flag ? 'rgba(26,26,26,0.65)' : '#a8a8a8' }}>{cell.k}</div>
            <div style={{ fontSize: 20, fontWeight: 600, color: '#1a1a1a', margin: '8px 0 5px', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>{cell.v}</div>
            <div style={{ fontSize: 11, fontWeight: 500, color: cell.flag ? '#1a1a1a' : cell.tone }}>{cell.delta}</div>
          </div>
        ))}
      </div>

      {/* Performance Band */}
      <div style={{ display: 'flex', flexWrap: 'wrap', background: '#fff', border: '1px solid #e5e5e5', borderRadius: 13, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 18, overflow: 'hidden' }}>
        {/* YTD Return */}
        {(() => {
          // True YTD (Modified Dietz vs Dec-31 closes); null when price history is unavailable
          const ytd = performanceYTD?.period_return ?? null;
          return (
            <div
              title={performanceYTD?.methodology ? `${performanceYTD.methodology}${performanceYTD.coverage_pct != null ? ` · coverage ${performanceYTD.coverage_pct}%` : ''}` : undefined}
              style={{ flex: 1, minWidth: 120, padding: '16px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
              <div style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600, color: '#a8a8a8' }}>YTD Return</div>
              <div style={{ fontSize: 20, fontWeight: 600, color: ytd != null ? (ytd >= 0 ? '#059669' : '#e53535') : '#1a1a1a', margin: '8px 0 5px', letterSpacing: '-0.01em' }}>
                {ytd != null ? formatPercent(ytd) : '—'}
              </div>
              <div style={{ fontSize: 11, fontWeight: 500, color: '#a8a8a8' }}>Public markets</div>
            </div>
          );
        })()}
        <div style={{ width: 1, background: '#e5e5e5', alignSelf: 'stretch' }} />
        {/* 3-Year Return */}
        {(() => {
          const t3y = performance3Y?.annualized_return ?? null;
          return (
            <div style={{ flex: 1, minWidth: 120, padding: '16px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
              <div style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600, color: '#a8a8a8' }}>3-Year Return</div>
              <div style={{ fontSize: 20, fontWeight: 600, color: t3y != null ? (t3y >= 0 ? '#059669' : '#e53535') : '#1a1a1a', margin: '8px 0 5px', letterSpacing: '-0.01em' }}>
                {t3y != null ? formatPercent(t3y) : '—'}
              </div>
              <div style={{ fontSize: 11, fontWeight: 500, color: '#a8a8a8' }}>Annualised{performance3Y?.period_start ? ` · since ${new Date(performance3Y.period_start).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}` : ''}</div>
            </div>
          );
        })()}
        <div style={{ width: 1, background: '#e5e5e5', alignSelf: 'stretch' }} />
        {/* Avg IRR */}
        <div style={{ flex: 1, minWidth: 120, padding: '16px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
          <div style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600, color: '#a8a8a8' }}>Avg. IRR</div>
          <div style={{ fontSize: 20, fontWeight: 600, color: avgIRR != null ? (avgIRR >= 0 ? '#059669' : '#e53535') : '#1a1a1a', margin: '8px 0 5px', letterSpacing: '-0.01em' }}>
            {avgIRR != null ? `${avgIRR.toFixed(1)}%` : '—'}
          </div>
          <div style={{ fontSize: 11, fontWeight: 500, color: '#a8a8a8' }}>Private markets</div>
        </div>
        {/* Benchmark cells */}
        {benchmarks.map((bm) => (
          <React.Fragment key={bm.benchmark}>
            <div style={{ width: 1, background: '#e5e5e5', alignSelf: 'stretch' }} />
            <div style={{ flex: 1, minWidth: 120, padding: '16px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
              <div style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600, color: '#a8a8a8' }}>{bm.benchmark_name}</div>
              <div style={{ fontSize: 20, fontWeight: 600, color: '#1a1a1a', margin: '8px 0 5px', letterSpacing: '-0.01em' }}>
                {bm.benchmark_return != null ? `${bm.benchmark_return.toFixed(1)}%` : '—'}
              </div>
              <div style={{ fontSize: 11, fontWeight: 500, color: '#a8a8a8' }}>YTD benchmark</div>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Category Rows */}
      {/* <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '6px 0 10px' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#2a2a2a', letterSpacing: '-0.01em' }}>Asset Class Overview</span>
        </div>
        {[
          {
            glyph: <CandlestickChart size={19} />,
            title: 'Public Markets',
            meta: `${publicMarkets?.holdings_count || 0} holdings across asset classes`,
            kpis: [
              { k: 'Invested', v: formatCurrency(publicInvested, showValues) },
              { k: 'FMV', v: formatCurrency(publicValue, showValues) },
              { k: 'Return', v: formatPercent(publicGainPercent), tone: publicGainPercent >= 0 ? '#059669' : '#e53535' },
            ],
            path: '/markets',
          },
          {
            glyph: <Rocket size={19} />,
            title: 'Private Markets',
            meta: `${privateEquity.portfolioCompanyCount || 0} companies · ${privateEquity.dealsInProgress || 0} active deals`,
            kpis: [
              { k: 'AUM', v: formatCurrency(pmAUMTotal, showValues) },
              { k: 'Invested', v: formatCurrency(Object.values(pmData).flat().reduce((s,d)=>s+(d.invested||0),0), showValues) },
              { k: 'Closed', v: String(closedDealsCount) },
            ],
            path: '/private-markets',
          },
          {
            glyph: <Wallet size={19} />,
            title: 'Cash & Alternatives',
            meta: 'Liquid assets, fixed deposits & alternative funds',
            kpis: [
              { k: 'Cash', v: formatCurrency((altData.cash||[]).reduce((s,d)=>s+(d.balance||0),0), showValues) },
              { k: 'FDs', v: formatCurrency((altData.fd||[]).reduce((s,d)=>s+(d.current_value||d.avg_price||0),0), showValues) },
              { k: 'Alts', v: formatCurrency((altData.alternatives||[]).reduce((s,d)=>s+(d.nav||0),0), showValues) },
            ],
            path: '/cash-alternatives',
          },
          {
            glyph: <Home size={19} />,
            title: 'Others',
            meta: `Real estate, gold, art & intellectual property`,
            kpis: [
              { k: 'Real Estate', v: formatCurrency(realEstate.totalValue, showValues) },
              { k: 'Yield', v: realEstate.portfolioYield > 0 ? `${realEstate.portfolioYield.toFixed(1)}%` : '—' },
              { k: 'Properties', v: String(realEstate.properties.length) },
            ],
            path: '/others',
          },
        ].map((row, i) => (
          <button key={i} onClick={() => navigate(row.path)} style={{
            width: '100%', display: 'grid', gridTemplateColumns: 'auto 1.3fr 1.4fr auto', alignItems: 'center', gap: 22, textAlign: 'left',
            background: '#fff', border: '1px solid #e5e5e5', borderRadius: 13, padding: '18px 22px', marginBottom: 10,
            boxShadow: '0 1px 4px rgba(0,0,0,0.05)', cursor: 'pointer', transition: 'box-shadow 0.2s, transform 0.2s, border-color 0.2s',
          }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.09)'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.borderColor = '#fed879'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = ''; e.currentTarget.style.borderColor = '#e5e5e5'; }}
          >
            <span style={{ width: 44, height: 44, borderRadius: 12, background: '#d9e2c3', color: '#1a1a1a', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
              {row.glyph}
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
              <span style={{ fontWeight: 600, fontSize: 15, color: '#2a2a2a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.title}</span>
              <span style={{ fontSize: 12, color: '#666' }}>{row.meta}</span>
            </div>
            <div style={{ display: 'flex', gap: 24 }}>
              {row.kpis.map((kpi, j) => (
                <div key={j} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#a8a8a8', fontWeight: 600, whiteSpace: 'nowrap' }}>{kpi.k}</span>
                  <span style={{ fontSize: 14.5, fontWeight: 600, color: kpi.tone || '#1a1a1a', fontVariantNumeric: 'tabular-nums' }}>{kpi.v}</span>
                </div>
              ))}
            </div>
            <span style={{ width: 34, height: 34, borderRadius: '50%', border: '1px solid #e5e5e5', display: 'grid', placeItems: 'center', color: '#1a1a1a' }}>
              <ChevronRight size={16} />
            </span>
          </button>
        ))}
      </div> */}

      {/* Tabs — detail views */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-white border border-[#e5e5e5]" style={{ borderRadius: 10 }}>
          <TabsTrigger value="overview" className="data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black">Overview</TabsTrigger>
          <TabsTrigger value="public" className="data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black">Public Markets</TabsTrigger>
          <TabsTrigger value="private" className="data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black">Private Markets</TabsTrigger>
          <TabsTrigger value="cash" className="data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black">Cash & Alts</TabsTrigger>
          <TabsTrigger value="watchlist" className="data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black flex items-center gap-1.5">
            <Star size={13} /> Watchlist
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Public Markets Summary */}
            <Card className="bg-white border-[#e5e5e5] shadow-sm flex flex-col h-full cursor-pointer hover:border-[#ffd87a] hover:shadow-md transition-all" onClick={() => navigate('/markets')}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-[#1a1a1a] flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#dae4c4]/40 flex items-center justify-center">
                    <CandlestickChart className="w-4 h-4 text-[#ffd87a]" />
                  </div>
                  Public Markets
                </CardTitle>
                <CardDescription>{publicMarkets?.holdings_count || 0} holdings across asset classes</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col flex-1">
                <div className="flex flex-col flex-1 gap-4">
                  {(() => {
                    const pubTotal = publicMarkets?.total_value || 0;
                    const chartData = assetClassSummary.length > 0
                      ? assetClassSummary
                          .filter(s => s.name !== 'Fixed Deposits' && (s.value || 0) > 0)
                          .sort((a, b) => b.value - a.value)
                          .slice(0, 6)
                          .map((s, i) => ({
                            name: s.name,
                            value: pubTotal > 0 ? (s.value / pubTotal) * 100 : 0,
                            color: DONUT_COLORS[i % DONUT_COLORS.length],
                          }))
                      : Object.entries(publicMarkets?.asset_allocation || {})
                          .filter(([key, p]) => p > 0 && key !== 'fd')
                          .sort(([, a], [, b]) => b - a)
                          .slice(0, 6)
                          .map(([asset, p], i) => ({
                            name: ASSET_LABEL_MAP[asset] || asset.replace('_', ' '),
                            value: p,
                            color: DONUT_COLORS[i % DONUT_COLORS.length],
                          }));
                    if (!chartData.length) return null;
                    const pubXirr = publicMarkets?.xirr;
                    return (
                      <div className="flex flex-col items-center gap-4">
                        <div className="grid grid-cols-3 gap-2 w-full">
                          <div className="p-2 bg-white border border-[#e5e5e5] rounded-lg text-center shadow-sm">
                            <p className="text-[10px] text-[#4a4a4a] uppercase tracking-wide">Invested</p>
                            <p className="text-sm font-bold text-[#1a1a1a] mt-0.5">{formatCurrency(publicMarkets?.total_invested || 0, showValues)}</p>
                          </div>
                          <div className="p-2 bg-white border border-[#e5e5e5] rounded-lg text-center shadow-sm">
                            <p className="text-[10px] text-[#4a4a4a] uppercase tracking-wide">FMV</p>
                            <p className="text-sm font-bold text-[#1a1a1a] mt-0.5">{formatCurrency(publicMarkets?.total_value || 0, showValues)}</p>
                          </div>
                          <div className="p-2 bg-white border border-[#e5e5e5] rounded-lg text-center shadow-sm">
                            <p className="text-[10px] text-[#4a4a4a] uppercase tracking-wide">Avg. XIRR</p>
                            <p className={`text-sm font-bold mt-0.5 ${pubXirr != null ? (pubXirr >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]') : 'text-[#1a1a1a]'}`}>
                              {pubXirr != null ? formatPercent(pubXirr) : '—'}
                            </p>
                          </div>
                        </div>
                        <DonutChart data={chartData} size={160} />
                        <div className="space-y-1.5 w-full">
                          {chartData.map((item, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                              <span className="text-xs text-[#4a4a4a] flex-1">{item.name}</span>
                              <span className="text-xs font-semibold text-[#1a1a1a]">{item.value.toFixed(1)}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  <Link to="/markets" className="mt-auto flex items-center justify-between p-3 rounded-lg border border-[#e5e5e5] hover:border-[#ffd87a] hover:shadow-sm transition-all">
                    <span className="text-sm font-medium text-[#1a1a1a]">View Public Markets</span>
                    <ChevronRight className="w-4 h-4 text-[#ffd87a]" />
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Private Markets Summary */}
            <Card className="bg-white border-[#e5e5e5] shadow-sm flex flex-col h-full cursor-pointer hover:border-[#ffd87a] hover:shadow-md transition-all" onClick={() => navigate('/private-markets')}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-[#1a1a1a] flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#dae4c4]/40 flex items-center justify-center">
                    <Rocket className="w-4 h-4 text-[#ffd87a]" />
                  </div>
                  Private Markets
                </CardTitle>
                <CardDescription>AUM by strategy</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col flex-1">
                {(() => {
                  const pmOthersValue = PM_OTHERS_CATS.reduce((s, k) => s + (pmData[k] || []).reduce((ss, d) => ss + (d.current_value || d.nav || 0), 0), 0);
                  const pmBars = [
                    { name: 'Startups',       value: privateEquity.totalValue || 0 },
                    { name: 'PE / VC',        value: [...(pmData.vc||[]),...(pmData.pe||[])].reduce((s,d)=>s+(d.current_value||0),0) },
                    { name: 'Search Funds',   value: (pmData.search||[]).reduce((s,d)=>s+(d.current_value||0),0) },
                    { name: 'Long-Only Eq.',  value: (pmData.long_only||[]).reduce((s,d)=>s+(d.current_value||0),0) },
                    { name: 'Private Credit', value: (pmData.credit||[]).reduce((s,d)=>s+(d.current_value||0),0) },
                    { name: 'Infrastructure', value: (pmData.infra||[]).reduce((s,d)=>s+(d.current_value||0),0) },
                    { name: 'Others',         value: pmOthersValue },
                  ].filter(d => d.value > 0).map((d, i) => ({ ...d, color: DONUT_COLORS[i % DONUT_COLORS.length] }));
                  const allPM = Object.values(pmData).flat();
                  const pmInvestedVal = allPM.reduce((s, d) => s + (d.invested || 0), 0);
                  const irrEntries = allPM.filter(d=>d.irr!=null);
                  const avgIRR = irrEntries.length ? (irrEntries.reduce((s,d)=>s+d.irr,0)/irrEntries.length).toFixed(1) : null;
                  return (
                    <div className="flex flex-col flex-1 gap-4">
                      <div className="grid grid-cols-3 gap-2">
                        <div className="p-2 bg-white border border-[#e5e5e5] rounded-lg text-center shadow-sm">
                          <p className="text-[10px] text-[#4a4a4a] uppercase tracking-wide">Invested</p>
                          <p className="text-sm font-bold text-[#1a1a1a] mt-0.5">{pmInvestedVal > 0 ? formatCurrency(pmInvestedVal, showValues) : '—'}</p>
                        </div>
                        <div className="p-2 bg-white border border-[#e5e5e5] rounded-lg text-center shadow-sm">
                          <p className="text-[10px] text-[#4a4a4a] uppercase tracking-wide">FMV</p>
                          <p className="text-sm font-bold text-[#1a1a1a] mt-0.5">{formatCurrency(pmAUMTotal, showValues)}</p>
                        </div>
                        <div className="p-2 bg-white border border-[#e5e5e5] rounded-lg text-center shadow-sm">
                          <p className="text-[10px] text-[#4a4a4a] uppercase tracking-wide">Avg. IRR</p>
                          <p className={`text-sm font-bold mt-0.5 ${avgIRR && parseFloat(avgIRR) >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>{avgIRR ? `${avgIRR}%` : '—'}</p>
                        </div>
                      </div>
                      {pmBars.length > 0 ? (
                        <div className="w-full" style={{ height: Math.max(140, pmBars.length * 36) }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={pmBars} layout="vertical" margin={{ left: 0, right: 12, top: 4, bottom: 4 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                              <XAxis type="number" tick={{ fontSize: 10, fill: '#4a4a4a' }} tickFormatter={v => formatCurrency(v, showValues).replace(/\s.*/,'')} />
                              <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#1a1a1a' }} width={84} />
                              <Tooltip formatter={(v) => [formatCurrency(v, showValues), 'AUM']} contentStyle={{ fontSize: 12, background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8, color: '#1a1a1a' }} itemStyle={{ color: '#ffd87a' }} labelStyle={{ color: '#1a1a1a' }} />
                              <Bar dataKey="value" barSize={16} radius={[0, 4, 4, 0]}>
                                {pmBars.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <div className="flex-1 flex items-center justify-center text-[#4a4a4a] text-sm">No fund data</div>
                      )}
                      <Link to="/private-markets" className="mt-auto flex items-center justify-between p-3 rounded-lg border border-[#e5e5e5] hover:border-[#ffd87a] hover:shadow-sm transition-all">
                        <span className="text-sm font-medium text-[#1a1a1a]">View Private Markets</span>
                        <ChevronRight className="w-4 h-4 text-[#ffd87a]" />
                      </Link>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Cash & Alternatives */}
            <Card className="bg-white border-[#e5e5e5] shadow-sm flex flex-col h-full cursor-pointer hover:border-[#ffd87a] hover:shadow-md transition-all" onClick={() => navigate('/cash-alternatives')}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-[#1a1a1a] flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#dae4c4]/40 flex items-center justify-center">
                    <Wallet className="w-4 h-4 text-[#ffd87a]" />
                  </div>
                  Cash & Alternatives
                </CardTitle>
                <CardDescription>Liquid assets, FDs & alternative funds</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col flex-1">
                {(() => {
                  const cashVal = (altData.cash||[]).reduce((s,d)=>s+(d.balance||0),0);
                  const fdVal   = (altData.fd||[]).reduce((s,d)=>s+(d.current_value||d.avg_price||0),0);
                  const altVal  = (altData.alternatives||[]).reduce((s,d)=>s+(d.nav||0),0);
                  const cashBars = [
                    { label: 'Cash',         value: cashVal, color: DONUT_COLORS[0] },
                    { label: 'Fixed Deps.',  value: fdVal,   color: DONUT_COLORS[2] },
                    { label: 'Alternatives', value: altVal,  color: DONUT_COLORS[4] },
                  ].filter(d => d.value > 0);
                  return (
                    <div className="flex flex-col flex-1 gap-4">
                      <div className="grid grid-cols-3 gap-2">
                        <div className="p-2 bg-white border border-[#e5e5e5] rounded-lg text-center shadow-sm">
                          <p className="text-[10px] text-[#4a4a4a] uppercase tracking-wide">Cash</p>
                          <p className="text-sm font-bold text-[#1a1a1a] mt-0.5">{formatCurrency(cashVal, showValues)}</p>
                        </div>
                        <div className="p-2 bg-white border border-[#e5e5e5] rounded-lg text-center shadow-sm">
                          <p className="text-[10px] text-[#4a4a4a] uppercase tracking-wide">FD</p>
                          <p className="text-sm font-bold text-[#1a1a1a] mt-0.5">{formatCurrency(fdVal, showValues)}</p>
                        </div>
                        <div className="p-2 bg-white border border-[#e5e5e5] rounded-lg text-center shadow-sm">
                          <p className="text-[10px] text-[#4a4a4a] uppercase tracking-wide">Alternatives</p>
                          <p className="text-sm font-bold text-[#1a1a1a] mt-0.5">{formatCurrency(altVal, showValues)}</p>
                        </div>
                      </div>
                      {cashBars.length > 0 ? (
                        <div className="w-full" style={{ height: 140 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={cashBars} layout="vertical" margin={{ left: 0, right: 12, top: 4, bottom: 4 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                              <XAxis type="number" tick={{ fontSize: 10, fill: '#4a4a4a' }} tickFormatter={v => formatCurrency(v, showValues).replace(/\s.*/,'')} />
                              <YAxis type="category" dataKey="label" tick={{ fontSize: 10, fill: '#1a1a1a' }} width={76} />
                              <Tooltip formatter={(v) => [formatCurrency(v, showValues), 'Value']} contentStyle={{ fontSize: 12, background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8, color: '#1a1a1a' }} itemStyle={{ color: '#ffd87a' }} labelStyle={{ color: '#1a1a1a' }} />
                              <Bar dataKey="value" barSize={16} radius={[0, 4, 4, 0]}>
                                {cashBars.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <div className="flex-1 flex items-center justify-center text-[#4a4a4a] text-sm">No data</div>
                      )}
                      <Link to="/cash-alternatives" className="mt-auto flex items-center justify-between p-3 rounded-lg border border-[#e5e5e5] hover:border-[#ffd87a] hover:shadow-sm transition-all">
                        <span className="text-sm font-medium text-[#1a1a1a]">View Cash & Alternatives</span>
                        <ChevronRight className="w-4 h-4 text-[#ffd87a]" />
                      </Link>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Public Markets Tab */}
        <TabsContent value="public" className="mt-6">
          <Card className="bg-white border-[#e5e5e5] shadow-sm">
            <CardHeader>
              <CardTitle className="text-base font-semibold text-[#1a1a1a] flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#dae4c4]/40 flex items-center justify-center">
                  <CandlestickChart className="w-4 h-4 text-[#ffd87a]" />
                </div>
                Public Markets Portfolio
              </CardTitle>
              <CardDescription>
                {formatCurrency(publicMarkets?.total_value || 0, showValues)} across {publicMarkets?.holdings_count || 0} holdings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-white border border-[#e5e5e5] rounded-lg shadow-sm">
                  <p className="text-xs font-medium text-[#4a4a4a] uppercase tracking-wide mb-1">Total Invested</p>
                  <p className="text-xl font-bold text-[#1a1a1a]">{formatCurrency(publicMarkets?.total_invested || 0, showValues)}</p>
                </div>
                <div className="p-4 bg-white border border-[#e5e5e5] rounded-lg shadow-sm">
                  <p className="text-xs font-medium text-[#4a4a4a] uppercase tracking-wide mb-1">Total Gain/Loss</p>
                  <p className={`text-xl font-bold ${(publicMarkets?.total_gain || 0) >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>
                    {formatCurrency(publicMarkets?.total_gain || 0, showValues)}
                  </p>
                </div>
                <div className="p-4 bg-white border border-[#e5e5e5] rounded-lg shadow-sm">
                  <p className="text-xs font-medium text-[#4a4a4a] uppercase tracking-wide mb-1">Returns</p>
                  <p className={`text-xl font-bold ${publicGainPercent >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>
                    {formatPercent(publicGainPercent)}
                  </p>
                </div>
              </div>
              <Button onClick={() => navigate('/markets')} className="w-full bg-[#ffd87a] hover:bg-[#f5cc6a] text-black font-medium">
                Go to Public Markets Dashboard
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Private Markets Tab */}
        <TabsContent value="private" className="mt-6">
          <Card className="bg-white border-[#e5e5e5] shadow-sm">
            <CardHeader>
              <CardTitle className="text-base font-semibold text-[#1a1a1a] flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#dae4c4]/40 flex items-center justify-center">
                  <Rocket className="w-4 h-4 text-[#ffd87a]" />
                </div>
                Private Markets Pipeline
              </CardTitle>
              <CardDescription>
                {privateEquity.portfolioCompanyCount} companies · {privateEquity.dealsInProgress} active deals
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-white border border-[#e5e5e5] rounded-lg shadow-sm">
                  <p className="text-xs font-medium text-[#4a4a4a] uppercase tracking-wide mb-1">Pipeline AUM</p>
                  <p className="text-xl font-bold text-[#1a1a1a]">{formatCurrency(privateEquity.totalValue, showValues)}</p>
                </div>
                <div className="p-4 bg-white border border-[#e5e5e5] rounded-lg shadow-sm">
                  <p className="text-xs font-medium text-[#4a4a4a] uppercase tracking-wide mb-1">Active Deals</p>
                  <p className="text-xl font-bold text-[#1a1a1a]">{privateEquity.dealsInProgress}</p>
                </div>
                <div className="p-4 bg-white border border-[#e5e5e5] rounded-lg shadow-sm">
                  <p className="text-xs font-medium text-[#4a4a4a] uppercase tracking-wide mb-1">Closed Deals</p>
                  <p className="text-xl font-bold text-emerald-600">{privateEquity.closedDeals}</p>
                </div>
              </div>
              <div className="space-y-2 mb-6">
                <h4 className="text-sm font-semibold text-[#1a1a1a] mb-3">Recent Deals</h4>
                {privateEquity.deals.slice(0, 5).map((deal, index) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-background hover:bg-white border border-transparent hover:border-[#e5e5e5] hover:shadow-sm transition-all">
                    <div>
                      <p className="text-sm font-medium text-[#1a1a1a]">{deal.deal_name || deal.company_name}</p>
                      <p className="text-xs text-[#4a4a4a]">{deal.sector} • {formatCurrency(deal.target_investment_amount, showValues)}</p>
                    </div>
                    <Badge className={
                      deal.stage === 'closed' ? 'bg-emerald-100 text-emerald-700 border-0' :
                      deal.stage === 'passed' ? 'bg-[#c97a3a]/15 text-[#c97a3a] border-0' :
                      'bg-[#ffd87a]/15 text-[#1a1a1a] border-0'
                    }>
                      {deal.stage}
                    </Badge>
                  </div>
                ))}
              </div>
              <Button onClick={() => navigate('/deals')} className="w-full bg-[#ffd87a] hover:bg-[#f5cc6a] text-black font-medium">
                Go to Deal Pipeline
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cash & Alternatives Tab */}
        <TabsContent value="cash" className="mt-6">
          <Card className="bg-white border-[#e5e5e5] shadow-sm">
            <CardHeader>
              <CardTitle className="text-base font-semibold text-[#1a1a1a] flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#dae4c4]/40 flex items-center justify-center">
                  <Wallet className="w-4 h-4 text-[#ffd87a]" />
                </div>
                Cash & Alternatives
              </CardTitle>
              <CardDescription>
                {formatCurrency(cashAltAUMTotal, showValues)} total · liquid assets &amp; alternatives
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-white border border-[#e5e5e5] rounded-lg shadow-sm">
                  <p className="text-xs font-medium text-[#4a4a4a] uppercase tracking-wide mb-1">Cash & Equivalents</p>
                  <p className="text-xl font-bold text-[#1a1a1a]">{formatCurrency((altData.cash || []).reduce((s, d) => s + (d.balance || 0), 0), showValues)}</p>
                  <p className="text-xs text-[#4a4a4a] mt-1">{(altData.cash || []).length} accounts</p>
                </div>
                <div className="p-4 bg-white border border-[#e5e5e5] rounded-lg shadow-sm">
                  <p className="text-xs font-medium text-[#4a4a4a] uppercase tracking-wide mb-1">Fixed Deposits</p>
                  <p className="text-xl font-bold text-[#1a1a1a]">{formatCurrency((altData.fd || []).reduce((s, d) => s + (d.current_value || d.avg_price || 0), 0), showValues)}</p>
                  <p className="text-xs text-[#4a4a4a] mt-1">{(altData.fd || []).length} deposit{(altData.fd || []).length !== 1 ? 's' : ''}</p>
                </div>
                <div className="p-4 bg-white border border-[#e5e5e5] rounded-lg shadow-sm">
                  <p className="text-xs font-medium text-[#4a4a4a] uppercase tracking-wide mb-1">Alternatives</p>
                  <p className="text-xl font-bold text-[#1a1a1a]">{formatCurrency((altData.alternatives || []).reduce((s, d) => s + (d.nav || 0), 0), showValues)}</p>
                  <p className="text-xs text-[#4a4a4a] mt-1">{(altData.alternatives || []).length} funds</p>
                </div>
              </div>
              <Button onClick={() => navigate('/cash-alternatives')} className="w-full bg-[#ffd87a] hover:bg-[#f5cc6a] text-black font-medium">
                Go to Cash &amp; Alternatives
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Watchlist Tab */}
        <TabsContent value="watchlist" className="mt-6">
          <Card className="bg-white border-[#e5e5e5] shadow-sm">
            <CardHeader>
              <CardTitle className="text-base font-semibold text-[#1a1a1a] flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#dae4c4]/40 flex items-center justify-center">
                  <Star className="w-4 h-4 text-[#ffd87a]" />
                </div>
                Watchlist
              </CardTitle>
              <CardDescription>Track stocks &amp; private companies outside your Apex portfolio</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Search-to-add */}
              <div className="mb-6">
                <div className="relative">
                  <input
                    value={watchlistSearch}
                    onChange={e => handleWatchlistSearchChange(e.target.value)}
                    placeholder="Search any US or India listed stock, ETF… (e.g. NVIDIA, Reliance)"
                    className="w-full border border-[#e5e5e5] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#ffd87a] pr-10"
                  />
                  {watchlistSearchLoading && (
                    <RefreshCw size={14} className="absolute right-3 top-3 animate-spin text-[#a8a8a8]" />
                  )}
                </div>
                {watchlistSearchResults.length > 0 && (
                  <div className="mt-1 border border-[#e5e5e5] rounded-lg bg-white shadow-sm divide-y divide-[#f5f5f5] max-h-56 overflow-y-auto z-10 relative">
                    {watchlistSearchResults.map(r => (
                      <button
                        key={r.symbol}
                        onClick={() => handleAddWatchlistItem(r.symbol, r.name, r.exchange)}
                        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-[#fffbf0] text-left transition-colors"
                      >
                        <div>
                          <span className="font-semibold text-sm text-[#1a1a1a] mr-2">{r.symbol}</span>
                          <span className="text-xs text-[#a8a8a8]">{r.name}</span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Badge className="bg-[#f0f0f0] text-[#666] border-0 text-[10px]">{r.exchange}</Badge>
                          <Plus size={13} className="text-[#ffd87a]" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-2">
                  <label className="flex items-center gap-1.5 text-xs text-[#4a4a4a] cursor-pointer">
                    <input type="checkbox" checked={addingPrivate} onChange={e => setAddingPrivate(e.target.checked)} className="accent-[#ffd87a]" />
                    Private company (no live price)
                  </label>
                </div>
              </div>

              {/* List */}
              {watchlistLoading ? (
                <div className="text-center py-8"><RefreshCw className="w-6 h-6 animate-spin mx-auto text-[#ffd87a]" /></div>
              ) : watchlist.length === 0 ? (
                <div className="text-center py-10 text-[#4a4a4a] text-sm">
                  <Star className="w-10 h-10 mx-auto mb-3 text-[#e5e5e5]" />
                  No items yet. Search above to add NVIDIA, SpaceX, Reliance or any listed stock.
                </div>
              ) : (
                <div className="divide-y divide-[#f0f0f0]">
                  {watchlist.map(item => {
                    const q = watchlistQuotes[item.symbol];
                    const changePos = q ? q.change_percent >= 0 : null;
                    const clickable = !item.is_private;
                    return (
                      <div
                        key={item.symbol}
                        className={`flex items-center gap-4 py-3 -mx-2 px-2 rounded-lg transition-colors ${clickable ? 'cursor-pointer hover:bg-[#faf9f5]' : ''}`}
                        onClick={clickable ? () => handleOpenWatchlistDetail(item.symbol) : undefined}
                      >
                        <div className="w-9 h-9 rounded-lg bg-[#f5f5f0] flex items-center justify-center flex-shrink-0">
                          {item.is_favourite
                            ? <Star size={15} className="text-[#ffd87a] fill-[#ffd87a]" />
                            : item.is_private
                              ? <Lock size={15} className="text-[#a8a8a8]" />
                              : <TrendingUp size={15} className="text-[#a8c8a0]" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm text-[#1a1a1a]">{item.symbol}</span>
                            {item.is_favourite && <Badge className="bg-[#fffbf0] text-[#b8960a] border-[#ffd87a]/40 text-[10px]">Favourite</Badge>}
                            {item.is_private && <Badge className="bg-[#f0f0f0] text-[#666] border-0 text-[10px]">Private</Badge>}
                            {item.exchange && !item.is_private && (
                              <Badge className="bg-[#f0f0f0] text-[#888] border-0 text-[10px]">{item.exchange}</Badge>
                            )}
                          </div>
                          <span className="text-xs text-[#a8a8a8]">{item.name}</span>
                        </div>
                        {q ? (
                          <div className="text-right">
                            <div className="font-semibold text-sm text-[#1a1a1a]">${q.ltp?.toFixed(2)}</div>
                            <div className={`text-xs font-medium ${changePos ? 'text-emerald-600' : 'text-[#e53535]'}`}>
                              {changePos ? '+' : ''}{q.change_percent?.toFixed(2)}%
                            </div>
                          </div>
                        ) : (
                          <div className="text-xs text-[#a8a8a8] text-right">
                            {item.is_private ? 'No live price' : '—'}
                          </div>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRemoveWatchlist(item.symbol); }}
                          className="p-1.5 rounded-lg hover:bg-[#f5f5f5] text-[#c8c8c8] hover:text-[#e53535] transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Watchlist stock detail popup */}
      <Dialog open={!!watchlistDetailSymbol} onOpenChange={handleCloseWatchlistDetail}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {watchlistDetailLoading ? 'Loading...' : (watchlistDetail?.name || watchlistDetailSymbol)}
              {watchlistDetail && (
                <Badge className="bg-[#f0f0f0] text-[#888] border-0 text-[10px]">{watchlistDetail.exchange}</Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {watchlistDetailLoading ? (
            <div className="py-16 flex justify-center"><RefreshCw className="w-6 h-6 animate-spin text-[#ffd87a]" /></div>
          ) : watchlistDetail ? (
            <div className="space-y-5">
              <div className="flex items-end justify-between">
                <div>
                  <div className="text-3xl font-bold text-[#1a1a1a]">
                    ${watchlistDetail.current_price?.toFixed(2)}
                  </div>
                  <div className={`text-sm font-medium ${watchlistDetail.change >= 0 ? 'text-emerald-600' : 'text-[#e53535]'}`}>
                    {watchlistDetail.change >= 0 ? '+' : ''}{watchlistDetail.change?.toFixed(2)}
                    {' '}({watchlistDetail.change >= 0 ? '+' : ''}{watchlistDetail.change_percent?.toFixed(2)}%) today
                  </div>
                </div>
                <div className="flex gap-1.5">
                  {['1mo', '3mo', '6mo', '1y', '2y'].map(r => (
                    <button
                      key={r}
                      onClick={() => watchlistDetailRange !== r && handleChangeWatchlistRange(watchlistDetailSymbol, r)}
                      disabled={watchlistDetailChartLoading}
                      className={`text-xs px-2.5 py-1 rounded-md font-medium transition-colors disabled:opacity-60 ${
                        watchlistDetailRange === r
                          ? 'bg-[#ffd87a] text-black'
                          : 'bg-[#f5f5f5] text-[#666] hover:bg-[#eee]'
                      }`}
                    >
                      {r.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              <div className={`relative transition-opacity ${watchlistDetailChartLoading ? 'opacity-40' : 'opacity-100'}`}>
                <StockHistoryChart data={watchlistDetail.history} />
                {watchlistDetailChartLoading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <RefreshCw className="w-5 h-5 animate-spin text-[#ffd87a]" />
                  </div>
                )}
              </div>

              {watchlistDetail.returns && (
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(RETURN_LABELS).map(([key, label]) => {
                    const val = watchlistDetail.returns[key];
                    const pos = val >= 0;
                    return (
                      <div key={key} className="bg-[#f9f9f7] rounded-lg py-2 text-center">
                        <div className="text-[10px] text-[#a8a8a8] font-medium uppercase">{label}</div>
                        <div className={`text-sm font-semibold ${val == null ? 'text-[#a8a8a8]' : pos ? 'text-emerald-600' : 'text-[#e53535]'}`}>
                          {val == null ? '—' : `${pos ? '+' : ''}${val.toFixed(2)}%`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm border-t border-[#f0f0f0] pt-4">
                <div className="flex justify-between"><span className="text-[#a8a8a8]">Day High</span><span className="font-medium">${watchlistDetail.day_high?.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-[#a8a8a8]">Day Low</span><span className="font-medium">${watchlistDetail.day_low?.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-[#a8a8a8]">Previous Close</span><span className="font-medium">${watchlistDetail.previous_close?.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-[#a8a8a8]">Currency</span><span className="font-medium">{watchlistDetail.currency}</span></div>
                <div className="flex justify-between"><span className="text-[#a8a8a8]">{watchlistDetailRange.toUpperCase()} High</span><span className="font-medium">${watchlistDetail.fifty_two_week_high?.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-[#a8a8a8]">{watchlistDetailRange.toUpperCase()} Low</span><span className="font-medium">${watchlistDetail.fifty_two_week_low?.toFixed(2)}</span></div>
              </div>
            </div>
          ) : (
            <div className="py-10 text-center text-sm text-[#a8a8a8]">No data available.</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
